import { Result, Typography, Button } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { RequestError } from '../../redux/error/types'

interface ErrorComponentProps {
  error?: RequestError
  refetch?: () => void
}

// Extracts the backend error code from AxiosError.response.data if available.
// Prefers .message (e.g. "DIMENSION_UNAUTHORIZED") over .name (e.g. "UnauthorizedError")
// because .message maps to the translation keys.
const getBackendErrorName = (error: any): string | undefined => {
  return error?.response?.data?.message || error?.response?.data?.name
}

const ErrorComponent = ({ error, refetch }: ErrorComponentProps) => {
  const { t } = useTranslation()

  const backendErrorName = getBackendErrorName(error)

  // Generate user-friendly error message
  const getErrorTitle = () => {
    if (!error) {
      return t('error:error-occurred', 'An error occurred')
    }

    // Try backend error name using the errorMessage namespace
    if (backendErrorName) {
      const translated = t(`errorMessage:${backendErrorName}`, '')
      if (translated) return translated
    }

    return t('errorMessage:DEFAULT', 'An error occurred')
  }

  // Generate error description
  const getErrorDescription = () => {
    if (!error) {
      return t('errorDescription:DEFAULT', '')
    }

    // Try backend error name using the errorDescription namespace
    if (backendErrorName) {
      const translated = t(`errorDescription:${backendErrorName}`, '')
      if (translated) return translated
    }

    return t('errorDescription:DEFAULT', '')
  }

  return (
    <Result
      status="500"
      title={getErrorTitle()}
      subTitle={<Typography.Text>{getErrorDescription()}</Typography.Text>}
      extra={
        refetch
          ? [
              <Button type="default" key="retry" onClick={() => refetch()}>
                {t('global:retry')}
              </Button>
            ]
          : undefined
      }
    />
  )
}

export default ErrorComponent
